import { useState } from "react";
import { useApp } from "../store.jsx";
import { taskEconomics, portfolioSummary, fmtSek, fmtMonths, fmtMonth, fmtHours } from "../calc.js";
import GanttChart from "../components/GanttChart.jsx";
import GatesEditor from "../components/GatesEditor.jsx";
import ArchivedTasks from "../components/ArchivedTasks.jsx";
import { PriorityPill, StatusPill } from "../components/Pill.jsx";

const PRIORITIES = [["P1","Quick Win"],["P2","High"],["P3","Medium"],["P4","Long-term"]];

export default function DeptPage({ dept }) {
  const { tasks, settings, meta, milestones, patchTask, reorderTasks, patchMilestone, addTask, duplicateTask } = useApp();
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState("P3");
  const [creating, setCreating] = useState(false);
  if (!tasks || !settings || !meta || !milestones) return <div className="container">Loading…</div>;

  const info = meta.departments[dept];
  if (!info) return <div className="container"><p>Unknown department.</p></div>;

  const deptTasks = tasks
    .filter(t => t.deptShort === dept)
    .sort((a,b) => (a.sortOrder - b.sortOrder) || (a.deptNum - b.deptNum));

  const deptMilestones = milestones
    .filter(m => m.deptShort === dept)
    .sort((a, b) => (a.sortOrder - b.sortOrder) || (a.month - b.month));
  const port = portfolioSummary(deptTasks, settings);

  async function onAddTask(e) {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title || creating) return;
    setCreating(true);
    try {
      const created = await addTask({ deptShort: dept, title, priority: newPriority });
      setNewTitle(""); setNewPriority("P3");
      if (created) window.location.hash = "#/task/" + created.id;
    } finally {
      setCreating(false);
    }
  }

  const activeDept   = deptTasks.filter(t => t.active !== false);
  const inactiveCount = deptTasks.length - activeDept.length;
  const counts = { P1:0, P2:0, P3:0, P4:0, Live:0, Building:0, Planned:0, Future:0 };
  activeDept.forEach(t => { counts[t.priority]++; counts[t.status]++; });

  return (
    <div className="container">
      <div className="crumb"><a href="#/">Overview</a><span className="sep">/</span>{info.name}</div>

      <div className="hero">
        <h1 style={{display:"inline-flex", alignItems:"center", gap:12}}>
          <span style={{display:"inline-block", width:14, height:14, borderRadius:3, background: info.color}}/>
          {info.fullname}
        </h1>
        <div className="sub">{info.headline}</div>
        <div className="sub" style={{marginTop:8, fontSize:12}}>Owners: {info.owners}</div>
      </div>

      <div className="kpi-row">
        <div className="kpi">
          <div className="l">Department build cost</div>
          <div className="v">{fmtSek(port.totalCostSek)}</div>
        </div>
        <div className="kpi">
          <div className="l">Monthly value at full deployment</div>
          <div className="v positive">{fmtSek(port.totalMonthlyValueSek)}</div>
        </div>
        <div className="kpi">
          <div className="l">Monthly hours saved</div>
          <div className="v positive">{fmtHours(port.totalMonthlyHoursSaved)}</div>
          <div className="s">
            {settings.hoursPerFteMonth > 0
              ? `≈ ${(port.totalMonthlyHoursSaved / settings.hoursPerFteMonth).toFixed(1)} FTE-equivalent`
              : null}
          </div>
        </div>
        <div className="kpi">
          <div className="l">Saved-hours value /mo</div>
          <div className="v positive">{fmtSek(port.totalHoursSavedValueSek)}</div>
        </div>
        <div className="kpi">
          <div className="l">New revenue /mo</div>
          <div className="v positive">{fmtSek(port.totalNewRevenueMonthlySek)}</div>
        </div>
        <div className="kpi">
          <div className="l">New revenue to Dec 2027</div>
          <div className="v positive">{fmtSek(port.totalNewRevenueHorizonSek)}</div>
        </div>
        <div className="kpi">
          <div className="l">Avg impact score</div>
          <div className="v">{port.avgScore.toFixed(1)}</div>
        </div>
        <div className="kpi">
          <div className="l">Dept break-even (simultaneous)</div>
          <div className="v">{fmtMonths(port.portfolioBreakevenMonths)}</div>
        </div>
        <div className="kpi">
          <div className="l">Dec 2027 net value</div>
          <div className={"v " + (port.totalHorizonEndValueSek >= 0 ? "positive" : "negative")}>
            {fmtSek(port.totalHorizonEndValueSek)}
          </div>
        </div>
      </div>

      <div className="statbar" style={{marginTop:14}}>
        <div className="stat"><div className="v">{activeDept.length}{inactiveCount ? <span style={{fontSize:12,color:"var(--muted)"}}> / {deptTasks.length}</span> : null}</div><div className="k">Active tasks</div></div>
        <div className="stat"><div className="v">{counts.Live}</div><div className="k">Live</div></div>
        <div className="stat"><div className="v">{counts.Building}</div><div className="k">Building</div></div>
        <div className="stat"><div className="v">{counts.Planned}</div><div className="k">Planned</div></div>
        <div className="stat"><div className="v">{counts.Future}</div><div className="k">Future</div></div>
        <div className="stat"><div className="v">{counts.P1}/{counts.P2}/{counts.P3}/{counts.P4}</div><div className="k">P1/P2/P3/P4</div></div>
        {inactiveCount > 0 && (
          <div className="stat"><div className="v" style={{color:"#fca5a5"}}>{inactiveCount}</div><div className="k">Disabled</div></div>
        )}
      </div>

      <h2 style={{marginTop:28, marginBottom:10}}>Key themes</h2>
      <div className="card">
        <ul style={{margin:0}}>{info.main_themes.map((t,i) => <li key={i} style={{marginBottom:6}}>{t}</li>)}</ul>
      </div>

      <h2 style={{marginTop:28, marginBottom:10}}>Implementation Gantt</h2>
      <div className="legend">
        <span><span className="sw" style={{background:"var(--p1)"}}/>P1 — Quick Win</span>
        <span><span className="sw" style={{background:"var(--p2)"}}/>P2 — High</span>
        <span><span className="sw" style={{background:"var(--p3)"}}/>P3 — Medium</span>
        <span><span className="sw" style={{background:"var(--p4)"}}/>P4 — Long-term</span>
        <span><span className="sw hatched"/>Investment recovery</span>
        <span><span className="sw" style={{background:"var(--positive-bg)", border:"1px solid var(--positive)"}}/>Net positive</span>
        <span><span className="be-mark"/>BE = Break-even</span>
        <span><span className="diamond"/>External gate</span>
        <span><span className="dep"/>Dependency</span>
      </div>
      <div className="ganttwrap">
        <GanttChart tasks={deptTasks} milestones={deptMilestones} settings={settings}
                    onMoveTask={patchTask} onReorderTasks={reorderTasks}
                    onMoveGate={patchMilestone}/>
      </div>
      <div className="sub" style={{marginTop:8}}>Drag a task's coloured bar horizontally to reschedule (half-month snaps); drag the title up or down to reorder; drag the diamond markers to move external gates. Click a bar or title to open its brief.</div>

      <h2 style={{marginTop:28, marginBottom:10}}>External gates</h2>
      <GatesEditor dept={dept}/>

      <h2 style={{marginTop:28, marginBottom:10}}>Task list</h2>
      <div className="card" style={{padding:0, overflow:"hidden"}}>
        <table className="tasks">
          <thead>
            <tr>
              <th title="Active — uncheck to exclude from totals">On</th>
              <th>#</th><th>Title</th><th>Priority</th><th>Status</th><th>Progress</th><th>Effort</th>
              <th>Hours/mo</th>
              <th title="Value of monthly hours saved (h/mo × hourly rate)">Saved-hours value/mo</th>
              <th title="New revenue per month">New revenue/mo</th>
              <th title="New revenue while live, through Dec 2027">New revenue to Dec 2027</th>
              <th title="Average of importance, quality and customer scores">Avg score</th>
              <th>Cost</th><th>Monthly value</th><th>Break-even</th><th>Dec 2027 net</th>
            </tr>
          </thead>
          <tbody>
            {deptTasks.map(t => {
              const e = taskEconomics(t, settings);
              const isActive = t.active !== false;
              return (
                <tr key={t.id} style={{ opacity: isActive ? 1 : 0.55 }}>
                  <td className="num">
                    <input type="checkbox" checked={isActive}
                           onChange={ev => patchTask(t.id, { active: ev.target.checked })}
                           style={{accentColor:"var(--link)"}}
                           title={isActive ? "Disable (exclude from totals)" : "Enable"}/>
                  </td>
                  <td className="num">#{t.briefNo}</td>
                  <td className="title">
                    <div style={{display:"flex", alignItems:"center", gap:8}}>
                      <a href={"#/task/"+t.id} style={{flex:"1 1 auto"}}>{t.title}</a>
                      <button
                        type="button"
                        className="btn"
                        title="Duplicate this task"
                        style={{padding:"2px 8px", fontSize:12}}
                        onClick={async () => {
                          const created = await duplicateTask(t.id);
                          if (created) window.location.hash = "#/task/" + created.id;
                        }}>
                        Copy
                      </button>
                    </div>
                    <div className="sub">{t.summary.slice(0,120)}{t.summary.length>120?"…":""}</div>
                  </td>
                  <td><PriorityPill priority={t.priority} label={t.priorityLabel} compact/></td>
                  <td>
                    <select value={t.status || "Planned"} className="status-select"
                            onChange={ev => patchTask(t.id, { status: ev.target.value })}>
                      <option value="Future">Future</option>
                      <option value="Planned">Planned</option>
                      <option value="Building">Building</option>
                      <option value="Live">Live</option>
                    </select>
                  </td>
                  <td className="num" style={{minWidth:100}}>
                    <div style={{display:"flex", alignItems:"center", gap:6, justifyContent:"flex-end"}}>
                      <span style={{fontVariantNumeric:"tabular-nums"}}>{Number(t.percentDone) || 0}%</span>
                    </div>
                    <div className="progress" title={`${Number(t.percentDone)||0}% done`}>
                      <div className="progress-bar" style={{width: (Number(t.percentDone)||0) + "%"}}/>
                    </div>
                  </td>
                  <td className="num">{Number(t.effortPw).toFixed(1)} pw · {Number(t.fteAllocation).toFixed(2)} FTE</td>
                  <td className="num positive" style={{color: Number(t.monthlyHoursSaved) > 0 ? "var(--positive)" : undefined}}>{fmtHours(Number(t.monthlyHoursSaved))}</td>
                  <td className="num" style={{color: e.valueBreakdown.hoursSavedSek > 0 ? "var(--positive)" : undefined}}>{fmtSek(e.valueBreakdown.hoursSavedSek)}</td>
                  <td className="num" style={{color: e.valueBreakdown.newRevenueSek > 0 ? "var(--positive)" : undefined}}>{fmtSek(e.valueBreakdown.newRevenueSek)}</td>
                  <td className="num" style={{color: e.newRevenueHorizonSek > 0 ? "var(--positive)" : undefined}}>{fmtSek(e.newRevenueHorizonSek)}</td>
                  <td className="num" title="Weighted average (see Settings)">{e.weightedAvgScore.toFixed(2)}</td>
                  <td className="num">{fmtSek(e.costSek)}</td>
                  <td className="num positive" style={{color:"var(--positive)"}}>{fmtSek(e.monthlyValueSek)}</td>
                  <td className="num">{fmtMonths(e.monthsToBreakeven)}<br/><span className="sub">→ {fmtMonth(e.breakevenMonth)}</span></td>
                  <td className="num" style={{color: e.horizonEndValueSek >= 0 ? "var(--positive)" : "#f87171"}}>{fmtSek(e.horizonEndValueSek)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <form className="task-add" onSubmit={onAddTask}>
        <input type="text" placeholder="New task title…" value={newTitle}
               onChange={e => setNewTitle(e.target.value)}/>
        <select value={newPriority} onChange={e => setNewPriority(e.target.value)}>
          {PRIORITIES.map(([code, lab]) => <option key={code} value={code}>{code} — {lab}</option>)}
        </select>
        <button className="btn primary" type="submit" disabled={!newTitle.trim() || creating}>
          {creating ? "Adding…" : "+ Add task"}
        </button>
      </form>
      <div className="sub" style={{marginTop:6}}>Creates the task here, then opens it so you can fill in the brief.</div>

      <ArchivedTasks dept={dept}/>
    </div>
  );
}
